import { useCallback, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { Media } from 'foundations-components/transitional/components/Media';
import { MediaBody } from 'foundations-components/transitional/components/MediaBody';
import { Link } from 'foundations-components/transitional/components/Link';
import { CONTENT_PLATFORM_PROPERTY_NAME, contentHasTranslations, getContentAssociatedObjects, getContentPropertyValue } from 'content-management-data-lib';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useExpandCollapseRow from 'framework-listing-lib/hooks/useExpandCollapseRow';
import { useFilterQuery } from 'framework-listing-lib/hooks/useListingLibFilters';
import useMultiLanguageGroup from 'framework-listing-lib/hooks/useMultiLanguageGroup';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { View } from 'foundations-components/components/View';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { LanguageIcon } from 'foundations-assets';
import { DownIcon, RightIcon } from 'foundations-assets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const WithCaretIconControl = ({
  crmObject,
  children
}) => {
  const objectId = crmObject.objectId;
  const {
    collapseRow,
    expandableObjectIds,
    expandRow
  } = useExpandCollapseRow();
  const isMultiLangGroupOpen = expandableObjectIds.includes(objectId);
  const toggleMultiLangGroupUsingCaret = useCallback(event => {
    event.stopPropagation();
    if (isMultiLangGroupOpen) {
      collapseRow(objectId);
    } else {
      expandRow(objectId);
    }
  }, [collapseRow, objectId, expandRow, isMultiLangGroupOpen]);
  return /*#__PURE__*/_jsx(MediaObject, {
    align: "center",
    spacing: "none",
    itemLeft: /*#__PURE__*/_jsx(View, {
      as: "span",
      marginInlineStart: "-600",
      marginBlockStart: "-500",
      position: "absolute",
      inlineSize: "24px",
      children: /*#__PURE__*/_jsx(IconButton, {
        onClick: toggleMultiLangGroupUsingCaret,
        use: "transparent"
        // aria-label={I18n.text(
        //   `content-management-ui.accessibility.table.translations.${
        //     isMultiLangGroupOpen ? 'close' : 'open'
        //   }`,
        //   { name: contentName }
        // )}
        ,
        "data-test-id": `accordion-${objectId}-${isMultiLangGroupOpen ? 'open' : 'closed'}`,
        size: "small",
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: isMultiLangGroupOpen ? DownIcon : RightIcon,
          color: "fill-info-default"
        })
      })
    }),
    children: children
  });
};
const WithTextControl = ({
  crmObject,
  children
}) => {
  const {
    objectId
  } = crmObject;
  const {
    expandableObjectIds
  } = useExpandCollapseRow();
  const translatedFromId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
  const isPrimaryGroupExpanded = !!translatedFromId && expandableObjectIds.includes(translatedFromId);
  const isGroupPrimary = !translatedFromId;
  const {
    hideMultiLanguageGroup,
    showMultiLanguageGroup
  } = useMultiLanguageGroup();
  const handleShowMultiLangGroup = useCallback(event => {
    event.preventDefault();
    const translatedContent = getContentAssociatedObjects(crmObject, 'OBJECT_TO_TRANSLATIONS');
    let translatedContentObjectIds = [];
    let primaryGroupObjectId = -1;
    if (isGroupPrimary) {
      primaryGroupObjectId = objectId;
      translatedContentObjectIds = translatedContent.map(translation => translation.objectId);
    } else {
      primaryGroupObjectId = translatedFromId;
      translatedContentObjectIds = [objectId, ...translatedContent.filter(translation => translation.objectId !== translatedFromId).map(translation => translation.objectId)];
    }
    showMultiLanguageGroup(primaryGroupObjectId, translatedContentObjectIds);
  }, [crmObject, isGroupPrimary, objectId, showMultiLanguageGroup, translatedFromId]);
  const handleHideMultiLangGroup = useCallback(event => {
    event.preventDefault();
    const primaryGroupObjectId = isGroupPrimary ? objectId : translatedFromId;
    hideMultiLanguageGroup(primaryGroupObjectId);
  }, [isGroupPrimary, objectId, translatedFromId, hideMultiLanguageGroup]);
  const toggleMultiLangGroupUsingText = useCallback(event => {
    event.stopPropagation();
    if (!isGroupPrimary && isPrimaryGroupExpanded) {
      handleHideMultiLangGroup(event);
    } else {
      handleShowMultiLangGroup(event);
    }
  }, [handleHideMultiLangGroup, handleShowMultiLangGroup, isGroupPrimary, isPrimaryGroupExpanded]);
  return /*#__PURE__*/_jsx(Media, {
    align: "center",
    children: /*#__PURE__*/_jsxs(MediaBody, {
      marginInlineStart: "0",
      children: [children, /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsxs(Link, {
            "data-test-id": `${isPrimaryGroupExpanded ? 'hide' : 'show'}-mLang-group-${objectId}`,
            onClick: toggleMultiLangGroupUsingText,
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: LanguageIcon,
              size: "200",
              padding: "0",
              marginInlineEnd: "100"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: `content-management-listing-lib.table.cells.multiLanguageGroupToggle.${isPrimaryGroupExpanded ? 'hide' : 'show'}`
            })]
          })
        })
      })]
    })
  });
};
const CellWithMultiLanguageGroupControls = ({
  crmObject,
  children
}) => {
  const filterQuery = useFilterQuery();
  const {
    hasAppliedFilters
  } = useCrmObjectTypeListingFilters();
  const isFilteringOrSearching = useMemo(() => Boolean(filterQuery || hasAppliedFilters), [filterQuery, hasAppliedFilters]);
  const hasTranslations = contentHasTranslations(crmObject);
  const {
    expandableObjectIds
  } = useExpandCollapseRow();
  const shouldDisplayShowOrHideMultiLangGroupText = useMemo(() => {
    const translatedFromId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
    const isPrimaryGroupExpanded = !!translatedFromId && expandableObjectIds.includes(translatedFromId);
    const isGroupPrimary = !translatedFromId;
    const isMultiLangGroupOpen = expandableObjectIds.includes(crmObject.objectId);
    const shouldDisplayHideMultiLangGroupText = !isGroupPrimary && isPrimaryGroupExpanded;
    const shouldDisplayShowMultiLangGroupText = !isGroupPrimary || hasTranslations && !isMultiLangGroupOpen;
    return isFilteringOrSearching && (shouldDisplayHideMultiLangGroupText || shouldDisplayShowMultiLangGroupText);
  }, [crmObject, expandableObjectIds, hasTranslations, isFilteringOrSearching]);
  if (hasTranslations && !isFilteringOrSearching) {
    // Render the caret icon control
    return /*#__PURE__*/_jsx(WithCaretIconControl, {
      crmObject: crmObject,
      children: children
    });
  }
  if (shouldDisplayShowOrHideMultiLangGroupText) {
    // Render the text button control
    return /*#__PURE__*/_jsx(WithTextControl, {
      crmObject: crmObject,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
export default CellWithMultiLanguageGroupControls;